import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Mail, Paperclip, Send, Bot, Clock, User, Users, Loader2, FileText, AlertTriangle, Link2, CheckCircle2, CheckCircle, XCircle, AtSign, Calendar, Tag, ExternalLink, Trash2, Reply, ReplyAll, Forward, X, ChevronDown, ChevronUp, FolderOpen, Plus, MailOpen, Mail as MailClosed, Eye, Sparkles, Image, ChevronLeft, ChevronRight } from 'lucide-react'
import { Card, Badge, Button, cn } from '../atoms'
import { AttachmentPicker } from '../molecules/AttachmentPicker'
import { RichTextEditor } from '../molecules/RichTextEditor'
import { EmailAutocomplete } from '../molecules/EmailAutocomplete'
import { QuotedTextCollapsible } from '../molecules/QuotedTextCollapsible'
import type { UploadedAttachment } from '../../hooks/useUploadAttachment'
import { useEmail, useProcessEmail, useGenerateResponse, useCreateCaseFromEmail, useDeleteEmail, useReplyEmail, useReplySignature, useGmailLabels, useUpdateEmailLabels, useCreateLabel, useMarkAsRead, useMarkAsUnread, useUpdateCriticidad, useToggleEmailHandled } from '../../hooks/useEmails'
import { useQueryClient } from '@tanstack/react-query'
import { useSuccessToast, useErrorToast } from '../atoms/Toaster'
import { getAssetUrl, getBackendUrl, sanitizeHtmlBody, rewriteSignatureImages } from '../../utils/assetUrl'
import { useAuth } from '../../store/auth.store'

export function EmailDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { data: email, isLoading } = useEmail(id!)
  const processMutation = useProcessEmail()
  const generateMutation = useGenerateResponse()
  const createCaseMutation = useCreateCaseFromEmail()
  const deleteMutation = useDeleteEmail()
  const replyMutation = useReplyEmail()
  const labelsQuery = useGmailLabels()
  const updateLabelsMutation = useUpdateEmailLabels()
  const markAsReadMutation = useMarkAsRead()
  const markAsUnreadMutation = useMarkAsUnread()
  const updateCriticidadMutation = useUpdateCriticidad()
  const toggleHandledMutation = useToggleEmailHandled()
  const successToast = useSuccessToast()
  const errorToast = useErrorToast()
  const [suggestedResponse, setSuggestedResponse] = useState('')
  const [tone, setTone] = useState<'formal' | 'friendly' | 'empathetic'>('formal')
  const [showReplyForm, setShowReplyForm] = useState(false)
  const replySignatureQuery = useReplySignature(id || '', showReplyForm)
  const [replyBody, setReplyBody] = useState('')
  const [replySubject, setReplySubject] = useState('')
  const [replyTo, setReplyTo] = useState('')
  const [replyCc, setReplyCc] = useState('')
  const [replyBcc, setReplyBcc] = useState('')
  const [showCc, setShowCc] = useState(false)
  const [showBcc, setShowBcc] = useState(false)
  const [replyAttachments, setReplyAttachments] = useState<UploadedAttachment[]>([])
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteFromGmail, setDeleteFromGmail] = useState(false)
  const [showLabelsDropdown, setShowLabelsDropdown] = useState(false)
  const [showCreateLabelForm, setShowCreateLabelForm] = useState(false)
  const [newLabelName, setNewLabelName] = useState('')
  const [showMoreMenu, setShowMoreMenu] = useState(false)
  const createLabelMutation = useCreateLabel()
  const markedAsReadRef = useRef(false)

  // Image preview / lightbox state
  const [lightboxImages, setLightboxImages] = useState<string[]>([])
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [showLightbox, setShowLightbox] = useState(false)

  const IMAGE_MIMES = /^image\/(jpeg|jpg|png|gif|webp|bmp|svg\+xml|tiff|tif)$/i
  const imageAttachments = (email?.attachments || []).filter((a: any) => a.mimeType && IMAGE_MIMES.test(a.mimeType))
  const otherAttachments = (email?.attachments || []).filter((a: any) => !a.mimeType || !IMAGE_MIMES.test(a.mimeType))

  const openLightbox = useCallback((images: string[], index: number) => {
    setLightboxImages(images)
    setLightboxIndex(index)
    setShowLightbox(true)
  }, [])

  const closeLightbox = useCallback(() => {
    setShowLightbox(false)
    setLightboxImages([])
    setLightboxIndex(0)
  }, [])

  const navigateLightbox = useCallback((dir: number) => {
    setLightboxIndex(prev => (prev + dir + lightboxImages.length) % lightboxImages.length)
  }, [lightboxImages.length])

  // Marcar como leído al abrir (y refrescar lista para reflejar el cambio)
  useEffect(() => {
    if (!id || !email) return
    if (email.isRead) {
      markedAsReadRef.current = true
      return
    }
    if (markedAsReadRef.current) return
    markedAsReadRef.current = true
    markAsReadMutation.mutate(id, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['emails'] })
        queryClient.setQueryData(['email', id], (old: any) => old ? { ...old, isRead: true, readAt: new Date().toISOString() } : old)
      },
    })
  }, [id, email?.isRead])

  // Procesa el HTML del email: convierte URLs relativas/absolutas de /uploads/ a absolutas con token
  const processEmailHtml = (html: string): string => {
    if (!html) return html
    // sanitizeHtmlBody agrega ?token= a todas las URLs de /uploads/ (tanto absolutas como relativas)
    let processed = sanitizeHtmlBody(html)

    // Reemplazar cid: references que no pudieron ser resueltas (imágenes inline perdidas)
    // Estas no se pueden mostrar sin las partes MIME originales
    processed = processed.replace(
      /<img\s+([^>]*?)src=(["'])cid:[^"' ]*\2([^>]*)>/gi,
      '<!-- [imagen inline no disponible] -->'
    )

    // Para hrefs de adjuntos, agregar target="_blank" si es un link a /uploads/
    processed = processed.replace(
      /<a\s+([^>]*?)href=(["'])(https?:\/\/[^"' ]*\/uploads\/[^"' ]*|["']\/uploads\/[^"' ]*)\2([^>]*)>/gi,
      (match, before, q, url, after) => {
        if (/target=/i.test(before + after)) return match
        return `<a ${before}href=${q}${url}${q} target="_blank" rel="noopener noreferrer"${after}>`
      }
    )

    // Agregar width/max-width a imágenes inline para que no se desborden
    processed = processed.replace(
      /<img\s+([^>]*?)>/gi,
      (match, attrs) => {
        if (/width=/i.test(attrs)) return match
        return `<img ${attrs} style="max-width:100%;height:auto;border-radius:6px;margin:8px 0">`
      }
    )

    return processed
  }

  /**
   * Convierte texto plano de email a HTML legible:
   * - *bold* → <strong>
   * - > quoted lines → bloque de cita
   * - URLs → links clickeables
   * - Saltos de línea → <br>
   */
  const plainTextToHtml = (text: string): string => {
    if (!text) return ''
    let html = text
      // Escapar HTML
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')

    // Líneas citadas (> text) → bloque de cita
    const lines = html.split('\n')
    const processedLines: string[] = []
    let inQuote = false

    for (const line of lines) {
      const isQuote = /^&gt;\s?/.test(line)
      if (isQuote && !inQuote) {
        processedLines.push('<div style="border-left:3px solid #d1d5db;padding:4px 12px;margin:8px 0;background:#f9fafb;color:#6b7280;font-size:13px;border-radius:0 6px 6px 0">')
        inQuote = true
      } else if (!isQuote && inQuote) {
        processedLines.push('</div>')
        inQuote = false
      }

      let processed = line
        // *bold* y **bold**
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<strong>$1</strong>')
        // URLs → links
        .replace(/(https?:\/\/[^\s<&]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" style="color:#2563eb;text-decoration:underline">$1</a>')
        // Emails
        .replace(/([\w.+-]+@[\w-]+\.[\w.-]+)/g, '<a href="mailto:$1" style="color:#2563eb;text-decoration:underline">$1</a>')

      if (isQuote) {
        processedLines.push(`<div style="margin:2px 0">${processed}</div>`)
      } else {
        processedLines.push(processed)
      }
    }
    if (inQuote) processedLines.push('</div>')

    return processedLines.join('\n')
  }

  const htmlToText = (html: string): string => {
    if (!html) return ''
    return html
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|h[1-6]|li|blockquote)>/gi, '\n')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ')
      .trim()
  }

  const handleProcess = async () => {
    if (!id) return
    try {
      const isReprocess = email?.isProcessed
      await processMutation.mutateAsync({ emailId: id, force: isReprocess })
      successToast(isReprocess ? 'Email re-procesado con IA' : 'Email procesado exitosamente')
    } catch {
      errorToast('Error al procesar el email')
    }
  }

  const handleGenerate = async () => {
    if (!id) return
    try {
      const res: any = await generateMutation.mutateAsync({ emailId: id, tone })
      setSuggestedResponse(res.suggestedResponse || '')
      if (res.warnings?.length) {
        res.warnings.forEach((w: string) => errorToast(w))
      }
    } catch {
      errorToast('Error al generar respuesta')
    }
  }

  const handleCreateCase = async () => {
    if (!id) return
    try {
      const res: any = await createCaseMutation.mutateAsync(id)
      successToast(`Caso creado: ${res.caseId}`)
    } catch (err: any) {
      errorToast(err?.response?.data?.message || 'Error al crear caso desde email')
    }
  }

  const handleDelete = async () => {
    if (!id) return
    try {
      await deleteMutation.mutateAsync({ emailId: id, fromGmail: deleteFromGmail })
      successToast(deleteFromGmail ? 'Email eliminado de la app y de Gmail' : 'Email eliminado de la app')
      setShowDeleteConfirm(false)
      navigate('/emails')
    } catch {
      errorToast('Error al eliminar el email')
    }
  }

  const handleReply = async () => {
    if (!id || !htmlToText(replyBody).trim() || replyMutation.isPending) return
    const toList = replyTo ? replyTo.split(/[,;]+/).map(e => e.trim()).filter(Boolean) : []
    const ccList = replyCc ? replyCc.split(/[,;]+/).map(e => e.trim()).filter(Boolean) : []
    const bccList = replyBcc ? replyBcc.split(/[,;]+/).map(e => e.trim()).filter(Boolean) : []

    const isForward = replySubject?.startsWith('Fwd:')
    const forwardQuote = isForward
      ? `<br><br><div style="border-left:3px solid #ccc;padding-left:12px;margin-top:16px;color:#666;">` +
        `<p><strong>---------- Mensaje reenviado ----------</strong></p>` +
        `<p><strong>De:</strong> ${email.from || ''}</p>` +
        `<p><strong>Fecha:</strong> ${email.receivedAt ? new Date(email.receivedAt).toLocaleString('es-CO') : ''}</p>` +
        `<p><strong>Asunto:</strong> ${email.subject || ''}</p>` +
        `<p><strong>Para:</strong> ${(email.to || []).join(', ')}</p>` +
        (email.cc?.length ? `<p><strong>CC:</strong> ${email.cc.join(', ')}</p>` : '') +
        `<hr style="border:none;border-top:1px solid #ddd;margin:8px 0;"/>` +
        (email.htmlBody || `<p>${(email.body || '').replace(/\n/g, '<br>')}</p>`) +
        `</div>`
      : ''

    replyMutation.mutate(
      {
        emailId: id,
        to: toList.length > 0 ? toList : undefined,
        subject: replySubject,
        body: htmlToText(replyBody),
        htmlBody: replyBody + forwardQuote,
        cc: ccList.length > 0 ? ccList : undefined,
        bcc: bccList.length > 0 ? bccList : undefined,
        attachments: replyAttachments.map(a => ({
          id: a.id, url: a.url, filename: a.filename, mimeType: a.mimeType, size: a.size, localPath: a.localPath,
        })),
      },
      {
        onSuccess: () => {
          successToast('Respuesta enviada correctamente')
          setShowReplyForm(false)
          setSuggestedResponse('')
          setReplyBody('')
          setReplySubject('')
          setReplyTo('')
          setReplyCc('')
          setReplyBcc('')
          setShowCc(false)
          setShowBcc(false)
          setReplyAttachments([])
        },
        onError: (err: any) => {
          errorToast(err?.message || 'Error al enviar respuesta')
        },
      }
    )
  }

  const handleToggleLabel = (labelId: string, isChecked: boolean) => {
    if (!id) return
    const currentLabels = email?.labels || []
    const add = isChecked && !currentLabels.includes(labelId) ? [labelId] : []
    const remove = !isChecked && currentLabels.includes(labelId) ? [labelId] : []
    updateLabelsMutation.mutateAsync({ emailId: id, addLabels: add, removeLabels: remove })
      .then(() => successToast(isChecked ? 'Label agregada' : 'Label removida'))
      .catch(() => errorToast('Error al actualizar labels'))
  }

  const handleCreateLabel = async () => {
    if (!newLabelName.trim()) return
    try {
      await createLabelMutation.mutateAsync(newLabelName.trim())
      successToast(`Label "${newLabelName.trim()}" creado en Gmail`)
      setNewLabelName('')
      setShowCreateLabelForm(false)
    } catch {
      errorToast('Error al crear label')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  if (!email) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Email no encontrado</p>
        <button onClick={() => navigate('/emails')} className="mt-4 text-primary-600 hover:underline">Volver</button>
      </div>
    )
  }

  return (
    <div className="space-y-4 max-w-[1600px] mx-auto">
      <button
        onClick={() => navigate('/emails')}
        className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-primary-700 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Volver a comunicaciones
      </button>

      {/* HEADER DEL EMAIL */}
      <Card className="overflow-hidden rounded-2xl border-slate-200 shadow-sm">
        {/* ═══════════════ BARRA DE ACCIONES ═══════════════ */}
        <div className="bg-white border-b border-slate-200">
          <div className="flex items-center gap-2 p-4 flex-wrap">
            {/* -- Acciones primarias -- */}
            <button
              onClick={() => {
                setShowReplyForm(true)
                setSuggestedResponse('')
                setReplySubject(`Re: ${email.subject}`)
                setReplyTo(email.from)
                setShowCc(false)
                setShowBcc(false)
              }}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm"
            >
              <Reply className="w-4 h-4" />
              Responder
            </button>
            <button
              onClick={() => {
                setShowReplyForm(true)
                setSuggestedResponse('')
                setReplySubject(`Re: ${email.subject}`)
                setReplyTo(email.from)
                setReplyCc((email.cc || []).join(', '))
                setShowCc(true)
                setShowBcc(false)
              }}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm"
            >
              <ReplyAll className="w-4 h-4" />
              Responder a todos
            </button>
            <button
              onClick={() => {
                const fwdHeader = `<p><strong>---------- Mensaje reenviado ----------</strong></p>` +
                  `<p><strong>De:</strong> ${email.from || ''}</p>` +
                  `<p><strong>Fecha:</strong> ${email.receivedAt ? new Date(email.receivedAt).toLocaleString('es-CO') : ''}</p>` +
                  `<p><strong>Asunto:</strong> ${email.subject || ''}</p>` +
                  `<p><strong>Para:</strong> ${(email.to || []).join(', ')}</p>` +
                  (email.cc?.length ? `<p><strong>CC:</strong> ${email.cc.join(', ')}</p>` : '') +
                  `<hr style="border:none;border-top:1px solid #ddd;margin:8px 0;"/>`
                const fwdBody = email.htmlBody
                  ? fwdHeader + email.htmlBody
                  : fwdHeader + `<p>${(email.body || '').replace(/\n/g, '<br>')}</p>`

                setShowReplyForm(true)
                setSuggestedResponse('')
                setReplySubject(`Fwd: ${email.subject}`)
                setReplyTo('')
                setReplyBody(fwdBody)
                setShowCc(false)
                setShowBcc(false)
              }}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm"
            >
              <Forward className="w-4 h-4" />
              Reenviar
            </button>

            <div className="h-6 w-px bg-gray-300 mx-1" />

            {/* -- Labels -- */}
            <div className="relative">
              <button
                onClick={() => setShowLabelsDropdown(!showLabelsDropdown)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm"
              >
                <Tag className="w-4 h-4" />
                Etiquetas
                {showLabelsDropdown ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
              {showLabelsDropdown && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowLabelsDropdown(false)} />
                  <div className="absolute left-0 z-20 mt-1 w-72 bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-1 max-h-80 overflow-y-auto">
                    <div className="flex items-center justify-between px-3 py-1.5">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Etiquetas de Gmail</p>
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowCreateLabelForm(true) }}
                        className="flex items-center gap-1 text-[10px] font-medium text-primary-600 hover:text-primary-700 px-1.5 py-0.5 rounded hover:bg-primary-50 transition-colors"
                      >
                        <Plus className="w-3 h-3" />
                        Nueva
                      </button>
                    </div>
                    {showCreateLabelForm && (
                      <div className="px-3 py-2 border-y border-gray-100 bg-gray-50/50">
                        <div className="flex gap-1.5">
                          <input
                            type="text"
                            placeholder="Nombre de la etiqueta..."
                            value={newLabelName}
                            onChange={(e) => setNewLabelName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleCreateLabel() }}
                            className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500"
                            autoFocus
                          />
                          <button
                            onClick={handleCreateLabel}
                            disabled={!newLabelName.trim() || createLabelMutation.isPending}
                            className="px-3 py-1.5 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 flex-shrink-0"
                          >
                            {createLabelMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Crear'}
                          </button>
                        </div>
                        <button
                          onClick={() => { setShowCreateLabelForm(false); setNewLabelName('') }}
                          className="mt-1 text-xs text-gray-400 hover:text-gray-600"
                        >
                          Cancelar
                        </button>
                      </div>
                    )}
                    {labelsQuery.isLoading ? (
                      <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div>
                    ) : (labelsQuery.data?.labels || []).length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-3">Sin etiquetas</p>
                    ) : (
                      (labelsQuery.data?.labels || []).map((label: any) => {
                        const isActive = (email.labels || []).includes(label.id)
                        return (
                          <label key={label.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors">
                            <input
                              type="checkbox"
                              checked={isActive}
                              onChange={(e) => handleToggleLabel(label.id, e.target.checked)}
                              className="rounded text-primary-600 focus:ring-primary-500 w-4 h-4"
                            />
                            <span className="text-sm text-gray-700">{label.name}</span>
                          </label>
                        )
                      })
                    )}
                  </div>
                </>
              )}
            </div>

            {/* -- Read/Unread + Delete -- */}
            <button
              onClick={() => {
                if (!id) return
                if (email.isRead) {
                  markAsUnreadMutation.mutate(id, {
                    onSuccess: () => {
                      queryClient.setQueryData(['email', id], (old: any) => old ? { ...old, isRead: false, readAt: null, readBy: null } : old)
                      successToast('Marcado como no leído')
                    },
                  })
                } else {
                  markAsReadMutation.mutate(id, {
                    onSuccess: () => {
                      queryClient.setQueryData(['email', id], (old: any) => old ? { ...old, isRead: true, readAt: new Date().toISOString() } : old)
                      successToast('Marcado como leído')
                    },
                  })
                }
              }}
              disabled={markAsReadMutation.isPending || markAsUnreadMutation.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm disabled:opacity-50"
              title={email.isRead ? 'Marcar como no leído' : 'Marcar como leído'}
            >
              {email.isRead ? <MailClosed className="w-4 h-4" /> : <MailOpen className="w-4 h-4" />}
            </button>

            {/* Botón de Estado de Gestión (Gestionado / Pendiente) */}
            {(() => {
              const isHandled = !!(email.isHandled || (email.case?.status && email.case.status?.isFinal))
              return (
                <button
                  onClick={() => {
                    if (toggleHandledMutation.isPending || !id) return
                    const nextState = !isHandled
                    toggleHandledMutation.mutate(
                      { emailId: id, isHandled: nextState },
                      {
                        onSuccess: () => successToast(nextState ? 'Correo marcado como gestionado' : 'Correo marcado como pendiente'),
                        onError: (err: any) => errorToast(err?.message || 'Error al cambiar estado de gestión'),
                      }
                    )
                  }}
                  disabled={toggleHandledMutation.isPending}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg border transition-all shadow-sm',
                    isHandled
                      ? 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-400'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300'
                  )}
                  title={isHandled ? 'Correo ya gestionado (clic para marcar como pendiente)' : 'Marcar este correo como gestionado'}
                >
                  {toggleHandledMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : isHandled ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  <span>{isHandled ? 'Gestionado' : 'Marcar gestionado'}</span>
                </button>
              )
            })()}

            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-white border border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 transition-all shadow-sm"
              title="Eliminar"
            >
              <Trash2 className="w-4 h-4" />
            </button>

            {/* -- Menú más (Crear caso, Procesar IA) -- */}
            <div className="relative">
              <button
                onClick={() => setShowMoreMenu(!showMoreMenu)}
                className="inline-flex items-center gap-1 px-2 py-2 text-sm font-medium rounded-lg bg-white border border-gray-300 text-gray-500 hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm"
                title="Más opciones"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><circle cx="4" cy="10" r="2"/><circle cx="10" cy="10" r="2"/><circle cx="16" cy="10" r="2"/></svg>
              </button>
              {showMoreMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowMoreMenu(false)} />
                  <div className="absolute right-0 z-20 mt-1 w-52 bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-1">
                    {!email.case && (
                      <button
                        onClick={() => { handleCreateCase(); setShowMoreMenu(false) }}
                        disabled={createCaseMutation.isPending}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                      >
                        {createCaseMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4 text-primary-600" />}
                        Crear caso
                      </button>
                    )}
                    <button
                      onClick={() => { handleProcess(); setShowMoreMenu(false) }}
                      disabled={processMutation.isPending}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      {processMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4 text-purple-600" />}
                      Procesar con IA
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* ═══════════════ CRITICIDAD ═══════════════ */}
            <div className="ml-auto flex items-center gap-2">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider hidden sm:inline">Criticidad</span>
              <div className="flex items-center gap-1">
                {(() => {
                  const CRITICITY_ORDER: Record<string, number> = {
                    INFORMATIVO: 0, BAJO: 1, MEDIO: 2, ALTO: 3, CRITICO: 4, CRITICO_MAXIMO: 5,
                  }
                  const currentLevel = CRITICITY_ORDER[email.criticidad || 'INFORMATIVO'] ?? 0
                  const caseIsOpen = !!(email.caseId && email.case?.status && !email.case.status.isFinal)
                  const isAdmin = user?.role === 'ADMIN'

                  return (['CRITICO_MAXIMO', 'CRITICO', 'ALTO', 'MEDIO', 'BAJO', 'INFORMATIVO'] as const).map((level) => {
                    const isActive = email.criticidad === level
                    const levelOrder = CRITICITY_ORDER[level] ?? 0
                    const isDegradation = levelOrder < currentLevel
                    const isBlocked = caseIsOpen && isDegradation && !isAdmin
                    const colors: Record<string, { active: string; inactive: string }> = {
                      CRITICO_MAXIMO: { active: 'bg-red-600 text-white border-red-600 shadow-red-200', inactive: 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100' },
                      CRITICO: { active: 'bg-red-500 text-white border-red-500 shadow-red-200', inactive: 'bg-red-50 text-red-500 border-red-200 hover:bg-red-100' },
                      ALTO: { active: 'bg-orange-500 text-white border-orange-500 shadow-orange-200', inactive: 'bg-orange-50 text-orange-500 border-orange-200 hover:bg-orange-100' },
                      MEDIO: { active: 'bg-yellow-500 text-white border-yellow-500 shadow-yellow-200', inactive: 'bg-yellow-50 text-yellow-600 border-yellow-200 hover:bg-yellow-100' },
                      BAJO: { active: 'bg-green-500 text-white border-green-500 shadow-green-200', inactive: 'bg-green-50 text-green-600 border-green-200 hover:bg-green-100' },
                      INFORMATIVO: { active: 'bg-blue-500 text-white border-blue-500 shadow-blue-200', inactive: 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100' },
                    }
                    const labels: Record<string, string> = {
                      CRITICO_MAXIMO: 'Crítico Máximo', CRITICO: 'Crítico', ALTO: 'Alto', MEDIO: 'Medio', BAJO: 'Bajo', INFORMATIVO: 'Informativo',
                    }
                    return (
                      <button
                        key={level}
                        onClick={() => {
                          if (isActive || isBlocked) return
                          updateCriticidadMutation.mutate(
                            { emailId: id!, criticidad: level },
                            {
                              onSuccess: () => {
                                successToast(`Clasificado como ${level}`)
                                queryClient.setQueryData(['email', id!], (old: any) => old ? { ...old, criticidad: level } : old)
                              },
                              onError: (err: any) => {
                                const msg = err?.message || 'Error al clasificar'
                                errorToast(msg)
                              },
                            }
                          )
                        }}
                        disabled={updateCriticidadMutation.isPending || isBlocked}
                        className={cn(
                          'px-2.5 py-1.5 text-[11px] font-semibold rounded-lg border-2 transition-all shadow-sm',
                          isActive ? colors[level].active : colors[level].inactive,
                          (updateCriticidadMutation.isPending || isBlocked) && 'opacity-50 cursor-not-allowed',
                          isBlocked && 'line-through',
                        )}
                        title={isBlocked ? `Solo un administrador puede degradar la criticidad mientras el caso ${email.case?.caseNumber} esté abierto` : labels[level]}
                      >
                        {labels[level]}
                      </button>
                    )
                  })
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* Info del email */}
        <div className="p-4 md:p-6 space-y-4">
          {/* Asunto */}
          <div className="border-b border-gray-100 pb-4">
            <h1 className="text-xl font-semibold text-gray-900">{email.subject || '(Sin asunto)'}</h1>
            <div className="flex items-center gap-2 mt-2">
              <span className={email.isProcessed ? 'px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800' : 'px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800'}>
                {email.isProcessed ? 'Procesado' : 'Sin procesar'}
              </span>
              {(() => {
                const isHandled = !!(email.isHandled || (email.case?.status && email.case.status?.isFinal))
                return (
                  <span className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold', isHandled ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' : 'bg-amber-100 text-amber-800 border border-amber-200')}>
                    {isHandled ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <Clock className="w-3 h-3 text-amber-600" />}
                    {isHandled ? (email.case?.status?.isFinal ? 'Gestionado (Caso cerrado)' : (email.handledBy ? `Gestionado (${email.handledBy})` : 'Gestionado')) : 'Pendiente de gestión'}
                  </span>
                )
              })()}
              {email.status && email.status !== 'received' && (
                <Badge variant={email.status === 'in_case' ? 'success' : 'default'} className="text-[10px] px-1.5 py-0">
                  {email.status === 'in_case' ? 'En caso' : email.status}
                </Badge>
              )}
            </div>
          </div>

          {/* From / To / Cc / Date - Grid */}
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-3">
              <div className="flex items-center gap-1.5 text-gray-500 w-16 flex-shrink-0">
                <User className="w-3.5 h-3.5" />
                <span className="text-xs font-medium uppercase">De</span>
              </div>
              <div className="flex-1">
                <span className="font-medium text-gray-900">{email.from}</span>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex items-center gap-1.5 text-gray-500 w-16 flex-shrink-0">
                <Users className="w-3.5 h-3.5" />
                <span className="text-xs font-medium uppercase">Para</span>
              </div>
              <div className="flex-1 flex flex-wrap gap-1">
                {(email.to || []).map((addr: string, i: number) => (
                  <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 text-xs border border-blue-100">
                    {addr}
                  </span>
                ))}
              </div>
            </div>

            {(email.cc || []).length > 0 && (
              <div className="flex items-start gap-3">
                <div className="flex items-center gap-1.5 text-gray-500 w-16 flex-shrink-0">
                  <AtSign className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium uppercase">Cc</span>
                </div>
                <div className="flex-1 flex flex-wrap gap-1">
                  {email.cc.map((addr: string, i: number) => (
                    <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-50 text-gray-600 text-xs border border-gray-100">
                      {addr}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {(email.bcc || []).length > 0 && (
              <div className="flex items-start gap-3">
                <div className="flex items-center gap-1.5 text-gray-500 w-16 flex-shrink-0">
                  <Tag className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium uppercase">Cco</span>
                </div>
                <div className="flex-1 flex flex-wrap gap-1">
                  {email.bcc.map((addr: string, i: number) => (
                    <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-50 text-gray-600 text-xs border border-gray-100">
                      {addr}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <div className="flex items-center gap-1.5 text-gray-500 w-16 flex-shrink-0">
                <Calendar className="w-3.5 h-3.5" />
                <span className="text-xs font-medium uppercase">Fecha</span>
              </div>
              <div className="flex-1 text-gray-700">
                {new Date(email.date).toLocaleString('es-CO', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>

            {email.mailbox && (
              <div className="flex items-start gap-3">
                <div className="flex items-center gap-1.5 text-gray-500 w-16 flex-shrink-0">
                  <Mail className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium uppercase">Buzón</span>
                </div>
                <div className="flex-1 text-gray-700">
                  {email.mailbox.name || email.mailbox.email}
                </div>
              </div>
            )}

            {/* INDICADOR DE LECTURA */}
            {email.isRead && (
              <div className="flex items-start gap-3">
                <div className="flex items-center gap-1.5 text-gray-500 w-16 flex-shrink-0">
                  <Eye className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium uppercase">Leído</span>
                </div>
                <div className="flex-1 text-gray-700">
                  <span className="text-sm">
                    {email.readBy ? `Por: ${email.readBy}` : 'Leído'}
                  </span>
                  {email.readAt && (
                    <span className="text-xs text-gray-500 ml-2">
                      {new Date(email.readAt).toLocaleString('es-CO', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* CUERPO DEL EMAIL */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 md:p-6">
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4 md:p-6 overflow-hidden">
            {email.htmlBody || email.body ? (
              <QuotedTextCollapsible
                htmlContent={email.htmlBody ? processEmailHtml(email.htmlBody) : undefined}
                text={email.body}
                cleanClassName="email-body-content"
              />
            ) : (
              <div className="text-center py-12">
                <Mail className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-400 italic">Sin contenido</p>
              </div>
            )}
          </div>
        </div>

        {/* ADJUNTOS */}
        {email.attachments && email.attachments.length > 0 && (
          <div className="border-t-2 border-primary-200 p-4 md:p-6 bg-gradient-to-br from-blue-50 to-indigo-50">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-1.5 bg-blue-100 rounded-lg">
                <Paperclip className="w-5 h-5 text-blue-700" />
              </div>
              <h3 className="text-base font-semibold text-gray-900">
                Archivos adjuntos <span className="text-sm font-normal text-gray-500">({email.attachments.length})</span>
              </h3>
            </div>

            {/* Image previews */}
            {imageAttachments.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <Image className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">Imágenes ({imageAttachments.length})</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {imageAttachments.map((att: any, index: number) => {
                    const fullUrl = getAssetUrl(att.url)
                    return (
                      <button
                        key={att.id || index}
                        onClick={() => openLightbox(imageAttachments.map(a => getAssetUrl(a.url)), index)}
                        className="group relative aspect-square bg-white rounded-xl border-2 border-blue-200 hover:border-primary-400 overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-zoom-in"
                      >
                        <img
                          src={fullUrl}
                          alt={att.filename}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement
                            target.style.display = 'none'
                            const parent = target.parentElement
                            if (parent) {
                              const fallback = document.createElement('div')
                              fallback.className = 'w-full h-full flex flex-col items-center justify-center bg-gray-100'
                              fallback.innerHTML = '<svg class="w-8 h-8 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg><span class="text-xs text-gray-500 mt-1 truncate max-w-full px-1">' + att.filename + '</span>'
                              parent.appendChild(fallback)
                            }
                          }}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                          <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 pt-6">
                          <p className="text-[11px] text-white truncate font-medium drop-shadow">{att.filename}</p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Other (non-image) attachments */}
            {otherAttachments.length > 0 && (
              <div>
                {imageAttachments.length > 0 && (
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-800">Otros archivos ({otherAttachments.length})</span>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {otherAttachments.map((att: any, index: number) => {
                    const fullUrl = getAssetUrl(att.url)
                    return (
                      <a
                        key={att.id || index}
                        href={fullUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={att.filename}
                        className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-xl border border-blue-200 dark:border-gray-600 hover:border-primary-400 dark:hover:border-primary-500 hover:shadow-md transition-all group"
                      >
                        <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                          <FileText className="w-5 h-5 text-blue-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-primary-700">
                            {att.filename}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {att.size ? `${(att.size / 1024).toFixed(1)} KB` : ''}
                            {att.mimeType ? ` · ${att.mimeType.split('/')[1] || att.mimeType}` : ''}
                          </p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-600 flex-shrink-0" />
                      </a>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* CASO ASOCIADO */}
        {email.case && (
          <div className="border-t border-gray-200 p-4 md:p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary-600" />
                Caso Asociado
              </h3>
              <button
                onClick={() => navigate(`/cases/${email.case!.id}`)}
                className="text-xs text-primary-600 hover:text-primary-800 hover:underline flex items-center gap-1"
              >
                <Link2 className="w-3 h-3" />
                Ver caso completo
              </button>
            </div>
            <div className="p-4 bg-primary-50 border border-primary-100 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{email.case.caseNumber} — {email.case.subject}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <Badge variant="default" className="text-[10px] px-1.5 py-0">{email.case.type.label || email.case.type.name}</Badge>
                    <Badge variant="default" className={email.case.priority.name === 'critical' || email.case.priority.name === 'high' ? 'text-red-600 border-red-200 bg-red-50 text-[10px] px-1.5 py-0' : 'text-[10px] px-1.5 py-0'}>
                      {email.case.priority.label || email.case.priority.name}
                    </Badge>
                    <Badge variant="default" className="text-[10px] px-1.5 py-0">{email.case.status.label || email.case.status.name}</Badge>
                    {email.case.assignee && (
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {email.case.assignee.firstName} {email.case.assignee.lastName}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ALERTAS DETECTADAS */}
        {email.detectedAlerts && email.detectedAlerts.length > 0 && (
          <div className="border-t border-gray-200 p-4 md:p-6">
            <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              Alertas Detectadas ({email.detectedAlerts.length})
            </h3>
            <div className="space-y-3">
              {email.detectedAlerts.map((alert: any) => (
                <div key={alert.id} className="p-4 bg-amber-50 border border-amber-100 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-gray-900">{alert.title}</span>
                        <Badge variant={(alert.severity?.name || alert.severity) === 'critical' ? 'danger' : 'warning'} className="text-[10px] px-1.5 py-0">
                          {alert.severity?.label || alert.severity?.name || (typeof alert.severity === 'string' ? alert.severity : 'Severidad')}
                        </Badge>
                        <Badge variant="default" className="text-[10px] px-1.5 py-0">
                          {alert.type?.label || alert.type?.name || (typeof alert.type === 'string' ? alert.type : 'Alerta')}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-600 line-clamp-2">{alert.description}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          {alert.status === 'OPEN' && <XCircle className="w-3 h-3 text-red-400" />}
                          {alert.status === 'RESOLVED' && <CheckCircle2 className="w-3 h-3 text-green-400" />}
                          {alert.status === 'ACKNOWLEDGED' && <CheckCircle2 className="w-3 h-3 text-blue-400" />}
                          {alert.status === 'IN_PROGRESS' && <Clock className="w-3 h-3 text-amber-400" />}
                          {alert.status}
                        </span>
                        <span>{new Date(alert.createdAt).toLocaleString('es-CO')}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate(`/alerts/${alert.id}`)}
                      className="ml-3 text-xs text-primary-600 hover:text-primary-800 hover:underline flex items-center gap-1 shrink-0"
                    >
                      <Link2 className="w-3 h-3" />
                      Ver alerta
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ANÁLISIS IA */}
        {email.aiAnalysis && (
          <div className="border-t border-gray-200 p-4 md:p-6">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Análisis de IA</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-600 font-medium">Tipo</p>
                <p className="text-sm font-semibold text-gray-900 mt-1">{email.aiAnalysis.caseType?.label || email.aiAnalysis.caseType?.name || 'N/A'}</p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg">
                <p className="text-xs text-yellow-600 font-medium">Prioridad</p>
                <p className="text-sm font-semibold text-gray-900 mt-1">{email.aiAnalysis.priority?.label || email.aiAnalysis.priority?.name || 'N/A'}</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-xs text-green-600 font-medium">Área sugerida</p>
                <p className="text-sm font-semibold text-gray-900 mt-1">{email.aiAnalysis.suggestedArea || 'N/A'}</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg">
                <p className="text-xs text-purple-600 font-medium">Confianza</p>
                <p className="text-sm font-semibold text-gray-900 mt-1">{email.aiAnalysis.confidence ? `${Math.round(email.aiAnalysis.confidence * 100)}%` : 'N/A'}</p>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════ FORMULARIO DE RESPUESTA ═══════════════ */}
        {showReplyForm && (
          <div className="border-t-2 border-primary-200 bg-white">
            {/* Header */}
            <div className="flex items-center justify-between px-4 md:px-6 py-3 bg-gray-50 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                {replySubject?.startsWith('Fwd:') ? <Forward className="w-4 h-4 text-primary-600" /> : <Reply className="w-4 h-4 text-primary-600" />}
                {replySubject?.startsWith('Fwd:') ? 'Reenviar' : 'Responder'}
              </h3>
              <button
                onClick={() => {
                  setShowReplyForm(false)
                  setSuggestedResponse('')
                  setReplyBody('')
                  setReplySubject('')
                  setReplyTo('')
                  setReplyCc('')
                  setReplyBcc('')
                  setShowCc(false)
                  setShowBcc(false)
                  setReplyAttachments([])
                }}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Campos To, CC, BCC, Asunto */}
            <div className="px-4 md:px-6 border-b border-gray-200">
              <div className="flex items-center gap-2 py-2">
                <span className="text-sm text-gray-500 w-12 flex-shrink-0">Para:</span>
                <EmailAutocomplete
                  value={replyTo}
                  onChange={setReplyTo}
                  placeholder="destinatario@email.com"
                  disabled={replyMutation.isPending}
                  className="flex-1"
                />
                <button
                  onClick={() => setShowCc(!showCc)}
                  className={cn("text-xs font-medium px-2 py-1 rounded transition-colors", showCc ? "text-primary-700 bg-primary-50" : "text-primary-600 hover:text-primary-800 hover:bg-primary-50")}
                >
                  CC
                </button>
                <button
                  onClick={() => setShowBcc(!showBcc)}
                  className={cn("text-xs font-medium px-2 py-1 rounded transition-colors", showBcc ? "text-primary-700 bg-primary-50" : "text-primary-600 hover:text-primary-800 hover:bg-primary-50")}
                >
                  CCO
                </button>
              </div>
              {showCc && (
                <div className="flex items-center gap-2 py-2 border-t border-gray-100">
                  <span className="text-sm text-gray-500 w-12 flex-shrink-0">CC:</span>
                  <EmailAutocomplete
                    value={replyCc}
                    onChange={setReplyCc}
                    placeholder="copia@email.com"
                    disabled={replyMutation.isPending}
                    className="flex-1"
                  />
                </div>
              )}
              {showBcc && (
                <div className="flex items-center gap-2 py-2 border-t border-gray-100">
                  <span className="text-sm text-gray-500 w-12 flex-shrink-0">CCO:</span>
                  <EmailAutocomplete
                    value={replyBcc}
                    onChange={setReplyBcc}
                    placeholder="copia-oculta@email.com"
                    disabled={replyMutation.isPending}
                    className="flex-1"
                  />
                </div>
              )}
              <div className="flex items-center gap-2 py-2 border-t border-gray-100">
                <span className="text-sm text-gray-500 w-12 flex-shrink-0">Asunto:</span>
                <input
                  type="text"
                  value={replySubject}
                  onChange={(e) => setReplySubject(e.target.value)}
                  className="flex-1 text-sm bg-transparent border-none outline-none text-gray-900 placeholder-gray-400"
                  placeholder="Asunto"
                />
              </div>
            </div>

            {/* ═══ Toolbar de IA + Editor ═══ */}
            <div className="px-4 md:px-6 pt-4">
              {/* Barra de IA inline */}
              <div className="flex items-center gap-2 mb-3 p-2.5 bg-purple-50 rounded-lg border border-purple-100">
                <Sparkles className="w-4 h-4 text-purple-500 flex-shrink-0" />
                <span className="text-xs text-purple-700 font-medium flex-shrink-0">IA</span>
                <select
                  value={tone}
                  onChange={(e) => setTone(e.target.value as any)}
                  className="text-xs border border-purple-200 rounded-md px-2 py-1.5 bg-white text-gray-700 focus:ring-1 focus:ring-purple-400"
                >
                  <option value="formal">Formal</option>
                  <option value="friendly">Amigable</option>
                  <option value="empathetic">Empático</option>
                </select>
                <button
                  onClick={handleGenerate}
                  disabled={generateMutation.isPending}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600 transition-all shadow-sm disabled:opacity-50 ml-auto"
                >
                  {generateMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  Sugerir con IA
                </button>
              </div>

              {/* Banner de sugerencia IA (cuando ya se generó) */}
              {suggestedResponse && (
                <div className="mb-3 p-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-purple-700 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      Respuesta sugerida por IA
                    </span>
                    <button onClick={() => setSuggestedResponse('')} className="text-gray-400 hover:text-gray-600 p-0.5 rounded hover:bg-white/60">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="bg-white rounded-md border border-purple-100 p-3 text-sm text-gray-700 max-h-40 overflow-y-auto" dangerouslySetInnerHTML={{ __html: suggestedResponse }} />
                  <div className="flex justify-end gap-2 mt-2">
                    <button
                      onClick={() => { setReplyBody(suggestedResponse); setSuggestedResponse('') }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-purple-600 text-white hover:bg-purple-700 transition-colors shadow-sm"
                    >
                      <CheckCircle2 className="w-3 h-3" />
                      Usar esta respuesta
                    </button>
                    <button
                      onClick={() => setSuggestedResponse('')}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      Descartar
                    </button>
                  </div>
                </div>
              )}

              {/* Editor */}
              <RichTextEditor
                value={replyBody}
                onChange={setReplyBody}
                placeholder="Escribe tu respuesta..."
                minHeight="180px"
                disabled={replyMutation.isPending}
              />
              <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50/70 p-3">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <span className="text-xs font-medium text-gray-500">Firma incluida automáticamente</span>
                  {replySignatureQuery.data && (
                    <span className="text-[11px] text-gray-400">
                      {replySignatureQuery.data.source === 'gmail' ? 'Firma de Gmail' : 'Firma corporativa'}
                    </span>
                  )}
                </div>
                {replySignatureQuery.isLoading ? (
                  <div className="flex items-center gap-2 py-3 text-xs text-gray-500">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Cargando firma...
                  </div>
                ) : replySignatureQuery.data?.html ? (
                  <div
                    className="overflow-x-auto text-sm text-gray-700 [&_img]:max-w-full"
                    dangerouslySetInnerHTML={{ __html: rewriteSignatureImages(replySignatureQuery.data.html || '') }}
                  />
                ) : (
                  <p className="py-2 text-xs text-gray-500">La firma se agregará al enviar.</p>
                )}
              </div>
              <AttachmentPicker value={replyAttachments} onChange={setReplyAttachments} disabled={replyMutation.isPending} className="mt-3" />
            </div>

            {/* Botones de acción */}
            <div className="flex items-center justify-end gap-2 px-4 md:px-6 py-3 border-t border-gray-100">
              <button
                onClick={() => {
                  setShowReplyForm(false)
                  setSuggestedResponse('')
                  setReplyBody('')
                  setReplySubject('')
                  setReplyTo('')
                  setReplyCc('')
                  setReplyBcc('')
                  setShowCc(false)
                  setShowBcc(false)
                  setReplyAttachments([])
                }}
                disabled={replyMutation.isPending}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleReply}
                disabled={!htmlToText(replyBody).trim() || replyMutation.isPending}
                className="inline-flex items-center gap-1.5 px-6 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
              >
                {replyMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Enviar
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* MODAL: Confirmar eliminación */}
      {showDeleteConfirm && (
        <>
          <div className="fixed inset-0 bg-black/40 dark:bg-black/60 z-30" onClick={() => setShowDeleteConfirm(false)} />
          <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-md p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-xl">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">¿Eliminar email?</h3>
                  <p className="text-sm text-gray-500">Esta acción no se puede deshacer.</p>
                </div>
              </div>
              <label className="flex items-center gap-2.5 p-3 rounded-xl border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={deleteFromGmail}
                  onChange={(e) => setDeleteFromGmail(e.target.checked)}
                  className="rounded text-red-600 focus:ring-red-500 w-4 h-4"
                />
                <span className="text-sm text-gray-700">También eliminar de Gmail (mover a papelera)</span>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(false)}>Cancelar</Button>
                <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleteMutation.isPending}>
                  {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
                  Eliminar
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══ IMAGE LIGHTBOX ═══ */}
      {showLightbox && lightboxImages.length > 0 && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-[110] p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Counter */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[110] px-3 py-1 bg-black/50 rounded-full text-white text-sm font-medium">
            {lightboxIndex + 1} / {lightboxImages.length}
          </div>

          {/* Navigation arrows */}
          {lightboxImages.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); navigateLightbox(-1) }}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-[110] p-3 bg-white/10 hover:bg-white/25 rounded-full text-white transition-colors"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); navigateLightbox(1) }}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-[110] p-3 bg-white/10 hover:bg-white/25 rounded-full text-white transition-colors"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Image */}
          <img
            src={lightboxImages[lightboxIndex]}
            alt="Vista previa"
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Thumbnail strip */}
          {lightboxImages.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[110] flex gap-2 p-2 bg-black/40 rounded-xl max-w-[80vw] overflow-x-auto">
              {lightboxImages.map((img, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex(i) }}
                  className={`w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all ${
                    i === lightboxIndex ? 'border-white scale-110' : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
